import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { PersonRemoveOutlined } from "@mui/icons-material";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import useContactData from "hooks/useContactData";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import { HugeiconsIcon } from "@hugeicons/react";
import { ChartRelationshipIcon } from "@hugeicons-pro/core-stroke-rounded";
import ContactType from "enums/ContactType";
import ContactDrawerRouter from "./ContactDrawerRouter";
import { ContactRouterDrawerType } from "enums/ContactRouterDrawerType";
import IndividualSearchForBusiness from "./IndividualSearchForBusiness";
import { ConfirmDialog, toast } from "@outpave/ds";

/**
 * Everything the invite flow reads off a contact. The callers hold three
 * different contact shapes (a table row, a contact form, and a payment's
 * `payTo`), so they map to this rather than the component reading
 * `contactType` on some and `type` on others.
 */
export type InviteTarget = {
  id: string;
  email: string;
  contactType: ContactType | null | undefined;
};

interface InviteContactModalProps {
  openInviteContactModal: boolean;
  contact?: InviteTarget;
  isMissingBank?: boolean;
  refetchSelectedPayToContact?: () => void;
  setOpenInviteContactModal: (open: boolean) => void;
}

export const InviteContactModal: React.FC<InviteContactModalProps> = ({
  openInviteContactModal,
  contact,
  isMissingBank = false, // used in payments
  refetchSelectedPayToContact = () => {},
  setOpenInviteContactModal,
}) => {
  const { handleInviteContact, specificLoadingStates } = useContactData({});
  const { t } = useTranslation();
  const [selectedIndividualId, setSelectedIndividualId] = useState("");
  const [hideContactModal, setHideContactModal] = useState(false);
  const [refetchIndex, setRefetchIndex] = useState(0);

  const contactDrawer = useDrawerRecord<{
    drawerType: ContactRouterDrawerType;
    contactId?: string;
    startIndividualWithBusinessId?: string;
  }>();

  const isBusiness = contact?.contactType === ContactType.Business;

  // The key itself, so `Trans` does the lookup and the interpolation. Passing
  // it a translated sentence worked only because a missing key falls back to
  // itself, which meant the tags inside it survived by luck rather than design.
  const inviteDescriptionKey =
    isBusiness && isMissingBank
      ? "InviteBusinessContactMissingBankDescription"
      : isBusiness
        ? "InviteBusinessContact"
        : isMissingBank
          ? "InviteContactMissingBankDescription"
          : "InviteContactDescription";

  const inviteTitle = isMissingBank
    ? "Payment Details Missing"
    : "Invite Contact";

  return (
    <>
      <ConfirmDialog
        open={openInviteContactModal && !hideContactModal}
        tone="primary"
        title={t(inviteTitle)}
        icon={<HugeiconsIcon icon={ChartRelationshipIcon} />}
        confirmLabel={t("Invite")}
        cancelLabel={t("Close")}
        onConfirm={async () => {
          if (!contact) return;
          try {
            setOpenInviteContactModal(false);
            await handleInviteContact(selectedIndividualId || contact.id);
            toast.success(t("Successfully invited contact"));
          } catch (err: unknown) {
            console.error(err);
            const message = err instanceof Error ? err.message : "";
            toast.error(message || t("Failed to invite contact"));
          }
        }}
        onCancel={() => {
          if (specificLoadingStates.invitingContact) return;
          setHideContactModal(false);
          setOpenInviteContactModal(false);
        }}
        confirmDisabled={isBusiness && !selectedIndividualId}
        busy={specificLoadingStates.invitingContact}
        message={
          <>
            <Trans
              i18nKey={inviteDescriptionKey}
              components={{
                contact_email: (
                  <Typography
                    component="span"
                    variant="inherit"
                    sx={{ color: "var(--color-text-brand)" }}
                  />
                ),
                // A real button: this branch made the link render for the
                // first time, and a clickable span cannot be tabbed to or
                // activated with Enter. No children — the words come from
                // inside the tag in the translation, which is why they read
                // in the reader's language.
                edit_contact: (
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={() => {
                      if (!contact) return;
                      contactDrawer.show({
                        drawerType: ContactRouterDrawerType.EditingContact,
                        contactId: contact.id,
                      });
                      setHideContactModal(true);
                    }}
                    // Sits inside a sentence, so it keeps the surrounding
                    // type rather than the button defaults.
                    sx={{
                      font: "inherit",
                      verticalAlign: "baseline",
                      padding: 0,
                    }}
                  />
                ),
              }}
              values={{ email: contact?.email }}
            />

            {isBusiness && contact && (
              <Box sx={{ paddingBlock: "var(--space-2)" }}>
                <IndividualSearchForBusiness
                  businessId={contact.id}
                  onSelectIndividual={(individual) => {
                    if (individual?.id === "create-contact") {
                      contactDrawer.show({
                        drawerType: ContactRouterDrawerType.NewContact,
                        startIndividualWithBusinessId: contact.id,
                      });
                      setHideContactModal(true);
                      setSelectedIndividualId("");
                    } else {
                      setSelectedIndividualId(individual ? individual.id : "");
                    }
                  }}
                  refetchIndex={refetchIndex}
                />
              </Box>
            )}
          </>
        }
      />

      {contactDrawer.record && (
        <ContactDrawerRouter
          open={contactDrawer.open}
          drawerType={contactDrawer.record.drawerType}
          contactId={contactDrawer.record.contactId}
          startIndividualWithBusinessId={
            contactDrawer.record.startIndividualWithBusinessId
          }
          onClose={() => {
            // Potentially Added Bank Account
            if (contactDrawer.record?.contactId) refetchSelectedPayToContact();
            contactDrawer.close();
          }}
          // The modal stays hidden while the drawer slides out: closing it can unmount this component, drawer and all.
          onAfterClose={() => {
            const editedContact = !!contactDrawer.record?.contactId;
            contactDrawer.afterClose();
            setHideContactModal(false);
            if (editedContact) setOpenInviteContactModal(false);
          }}
          refreshContacts={async () => {
            setRefetchIndex((prev) => prev + 1);
          }}
        />
      )}
    </>
  );
};

interface RemoveContactModalProps {
  openRemoveContactModal: boolean;
  setOpenRemoveContactModal: (open: boolean) => void;
  contactId: string;
  contactEmail: string;
  refreshContacts?: () => void;
}

export const RemoveContactModal: React.FC<RemoveContactModalProps> = ({
  openRemoveContactModal,
  setOpenRemoveContactModal,
  contactId,
  contactEmail,
  refreshContacts,
}) => {
  const { handleRemoveContact, specificLoadingStates } = useContactData({});
  const { t } = useTranslation();

  return (
    <>
      <ConfirmDialog
        open={openRemoveContactModal}
        tone="danger"
        title={t("Remove Contact")}
        busy={specificLoadingStates.deletingContact}
        icon={<PersonRemoveOutlined />}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={async () => {
          try {
            setOpenRemoveContactModal(false);
            await handleRemoveContact(contactId);
            refreshContacts && refreshContacts();
            toast.success(t("Successfully removed contact"));
          } catch (err: unknown) {
            console.error(err);
            const message = err instanceof Error ? err.message : "";
            toast.error(message || t("Failed to remove contact"));
          }
        }}
        onCancel={() => {
          if (specificLoadingStates.deletingContact) return;
          setOpenRemoveContactModal(false);
        }}
        message={
          <Trans
            i18nKey="RemoveContactDescription"
            components={{
              contact_email: (
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ color: "var(--color-text-brand)" }}
                />
              ),
            }}
            values={{ email: contactEmail }}
          />
        }
      />
    </>
  );
};
